import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: standard convex _generated import
import type { Id } from '../../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: standard convex _generated import
import { CustomerPolicyCard } from '@/components/insurance/CustomerPolicyCard';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft, AlertCircle, User, Mail, Phone, ClipboardList } from 'lucide-react';

export function CustomerDetailPage() {
  const navigate = useNavigate();
  const { customerId } = useParams<{ customerId: string }>();

  const customer = useQuery(
    api.customers.get,
    customerId ? { customerId: customerId as Id<'leads'> } : 'skip' // AS_JUSTIFIED: one-time Id cast from route param at component top
  );

  const policies = useQuery(
    api.insurance.policies.listByLead,
    customerId ? { leadId: customerId as Id<'leads'> } : 'skip' // AS_JUSTIFIED: one-time Id cast from route param at component top
  );

  const isLoading = customer === undefined || policies === undefined;

  const handleBack = useCallback(() => {
    navigate('/customers');
  }, [navigate]);

  if (isLoading) {
    return (
      <div className="min-h-full bg-background-secondary p-8">
        <div className="flex items-center gap-3 mb-6">
          <Skeleton className="h-9 w-24" />
          <Skeleton className="h-7 w-48" />
        </div>
        <div className="flex gap-6">
          <div className="w-[340px] shrink-0 space-y-4">
            <Skeleton className="h-48 w-full rounded-xl" />
            <Skeleton className="h-32 w-full rounded-xl" />
          </div>
          <div className="flex-1 space-y-4">
            <Skeleton className="h-40 w-full rounded-xl" />
            <Skeleton className="h-40 w-full rounded-xl" />
          </div>
        </div>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="min-h-full bg-background-secondary flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-foreground mb-1">Customer not found</h2>
          <p className="text-sm text-muted-foreground mb-4">
            This customer may have been deleted or you don&apos;t have access.
          </p>
          <Button variant="outline" onClick={handleBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Customers
          </Button>
        </div>
      </div>
    );
  }

  const createdDate = new Date(customer.createdAt).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

  return (
    <div className="min-h-full bg-background-secondary p-8">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="sm" onClick={handleBack}>
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back
        </Button>
        <h1 className="text-xl font-semibold text-foreground">
          {customer.name ?? 'Unknown Customer'}
        </h1>
      </div>

      {/* Two-column layout */}
      <div className="flex gap-6">
        {/* Left column — fixed width */}
        <div className="w-[340px] shrink-0">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <div className="rounded-lg bg-primary/10 p-2">
                  <User className="w-4 h-4 text-primary" />
                </div>
                Customer Info
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {customer.email && (
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="w-4 h-4 text-muted-foreground shrink-0" />
                  <span className="text-foreground truncate">{customer.email}</span>
                </div>
              )}
              {customer.phoneNumber && (
                <div className="flex items-center gap-2 text-sm">
                  <Phone className="w-4 h-4 text-muted-foreground shrink-0" />
                  <span className="text-foreground">{customer.phoneNumber}</span>
                </div>
              )}
              {customer.source && (
                <div className="text-sm">
                  <span className="text-muted-foreground">Source: </span>
                  <span className="text-foreground capitalize">{customer.source}</span>
                </div>
              )}
              <div className="text-sm">
                <span className="text-muted-foreground">Member since: </span>
                <span className="text-foreground">{createdDate}</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right column — flexible */}
        <div className="flex-1 space-y-4">
          <div className="flex items-center gap-2 mb-2">
            <ClipboardList className="w-4 h-4 text-muted-foreground" />
            <h2 className="text-sm font-medium text-foreground">Policies ({policies.length})</h2>
          </div>

          {policies.length === 0 && (
            <div className="text-center py-12 text-muted-foreground">
              <p className="text-sm">No policies linked to this customer yet.</p>
            </div>
          )}

          {policies.map(policy => {
            const startDate = policy.fieldValues.find(
              f => f.fieldLabel.toLowerCase() === 'start date'
            )?.value;
            const expiryDate = policy.fieldValues.find(
              f =>
                f.fieldLabel.toLowerCase() === 'expiry date' ||
                f.fieldLabel.toLowerCase() === 'end date'
            )?.value;
            const totalPremium = policy.fieldValues.find(
              f => f.fieldLabel.toLowerCase() === 'total premium'
            )?.value;

            return (
              <CustomerPolicyCard
                key={policy._id}
                policyId={policy._id}
                policyNumber={policy.policyNumber}
                insuranceTypeName={policy.insuranceTypeName}
                status={policy.status}
                startDate={startDate}
                expiryDate={expiryDate}
                totalPremium={totalPremium}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}
